<template>
    <div class="audio-upload-wrap">
        <div class="upload">
            <div v-if="loading" class="loading">
                <p class="name">{{ `上传中 ${progress}%` }}</p>
                <el-slider v-model="progress" :show-tooltip="false"></el-slider>
            </div>
            <template v-else>
                <el-upload
                    ref="audioUpload"
                    action
                    drag
                    :accept="accept"
                    :http-request="uploadFileHandle"
                    :show-file-list="false"
                    :disabled="loading || repairLoading || disabled"
                    :before-upload="beforeUploadHandle"
                >
                    <div class="audio-content">
                        <div class="icon"></div>
                        <div class="content">
                            <div class="describe">
                                <p>
                                    要求wav文件
                                    <br />
                                    立体声至少44.1 kHz采样率和16bit
                                </p>
                            </div>
                            <div class="button">
                                <div class="point"></div>
                                <el-button type="primary" icon="">{{ audioData.path ? '重新上传' : '上传音频' }}</el-button>
                            </div>
                        </div>
                    </div>
                </el-upload>
                <AudioPaly v-if="audioData.path || repairStatus === 'repairing' || repairStatus === 'failed' || repairStatus === 'repaired'" :audio-data="audioData" :show-delete="repairStatus === 'failed'" @onEdit="onEdit" @onDelete="onDelete"></AudioPaly>
            </template>
        </div>
    </div>
</template>
<script>
import axios from 'axios';
import AudioPaly from './AudioPaly';
import {uploadMusicCheckFix, fetchMusicCheckFixStatus} from '@/api/file';
import {audioKeyMap, audioBeforeUploadHandle} from '@/utils/audio';
export default {
    components: {
        AudioPaly,
    },
    props: {
        tarckData: {
            type: Object,
            default: () => {},
        },
        accept: {
            type: String,
            default: 'audio/wav,audio/x-wav',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        maxSize: {
            type: Number,
            default: 300,
        },
    },
    computed: {
        audioData() {
            const {tarckData, fileNmae, repairStatus} = this;
            const {format, size, duration, path, name, fileName} = tarckData;
            
            let displayPath = path;
            let displayFormat = format;
            let displaySize = size;
            let displayDuration = duration;
            let displayFileName = fileNmae || fileName || name;
            
            if (!displayPath && (repairStatus === 'repairing' || repairStatus === 'failed' || repairStatus === 'repaired')) {
                displayFormat = this.tempAudioInfo.format;
                displaySize = this.tempAudioInfo.size;
                displayDuration = this.tempAudioInfo.duration;
                displayFileName = this.tempAudioInfo.fileName;
            }
            
            if (!displayFormat || !displaySize) return {};
            
            const mSzie = displaySize / 1024 / 1024;
            const mDuration = this.$utils.convertSecondsToTimeFormat(displayDuration || 0);
            return {
                name: `${displayFileName}.${displayFormat}`, 
                size: `${mSzie.toFixed(2)}M`, 
                duration: mDuration, 
                path: displayPath
            };
        },
        submitDisabled() {
            return this.repairLoading || this.repairStatus === 'repairing' || this.repairStatus === 'failed';
        },
        footerNote() {
            if (this.repairLoading || this.repairStatus === 'repairing') {
                return {text: '音频检测/修复中，请稍候', type: 'warning'};
            }
            if (this.repairStatus === 'failed') {
                return {text: '请删除当前音频并重新上传', type: 'danger'};
            }
            return null;
        },
        inlineNote() {
            if (this.repairLoading || this.repairStatus === 'repairing') {
                return {
                    type: 'repairing',
                    tag: '修复中',
                    text: this.repairMsg
                };
            }
            if (this.repairStatus === 'repaired') {
                return {
                    type: 'repaired',
                    tag: '已自动修复',
                    text: '音频结构已修复，音质不变'
                };
            }
            if (this.repairStatus === 'failed') {
                return {
                    type: 'failed',
                    tag: '需重新上传',
                    text: this.errorMsg || '音频文件结构异常，系统无法自动修复。请重新导出 WAV/FLAC 后上传。'
                };
            }
            return null;
        },
    },
    data() {
        return {
            loading: false,
            uploadFile: null,
            progress: 0,
            fileNmae: '',
            errorMsg: null,
            repairLoading: false,
            repairStatus: null,
            repairMsg: '',
            tempAudioInfo: {},
            cancelled: false,
            uploadSource: null,
            pollCancelled: false,
        };
    },
    watch: {
        tarckData: {
            handler(newVal, oldVal) {
                if (newVal && newVal.path && newVal.uploadStatus === undefined) {
                    const oldPath = oldVal && oldVal.path;
                    if (!oldPath && this.repairStatus === 'repaired') {
                        return;
                    }
                    if (!this.repairStatus || oldPath !== newVal.path) {
                        this.resetState();
                    }
                }
            },
            immediate: true,
            deep: true
        },
        repairLoading: {
            handler() {
                this.syncFooterState();
            }
        },
        repairStatus: {
            handler() {
                this.syncFooterState();
            }
        }
    },
    mounted() {
        this.syncFooterState();
    },
    methods: {
        cancel() {
            this.cancelled = true;
            this.pollCancelled = true;
            if (this.uploadSource) {
                this.uploadSource.cancel('Upload cancelled by user');
            }
            this.resetState();
        },
        resetState() {
            this.cancelled = false;
            this.pollCancelled = false;
            this.repairStatus = null;
            this.repairMsg = '';
            this.repairLoading = false;
            this.errorMsg = null;
            this.progress = 0;
            this.loading = false;
            this.tempAudioInfo = {};
            this.uploadSource = null;
            this.syncFooterState();
        },
        syncFooterState() {
            this.$emit('footer-state-change', {
                submitDisabled: this.submitDisabled,
                footerNote: this.footerNote,
                inlineNote: this.inlineNote,
            });
        },
        async getId3Info(file, name) {
            const fileMeta = await this.$utils.id3Read(file.file);
            if (fileMeta.state) {
                const {artist, genre, lyrics} = fileMeta.result;
                this.$emit('id3Update', {name, artist, genre, lyrics});
            }
        },
        resetUploadData() {
            this.loading = false;
            this.progress = 0;
            this.uploadFile = null;
        },
        resetRepairData() {
            this.repairStatus = null;
            this.repairMsg = '';
            this.errorMsg = null;
            this.tempAudioInfo = {};
        },
        async uploadFileHandle({file}) {
            try {
                this.cancelled = false;
                this.pollCancelled = false;
                this.uploadSource = axios.CancelToken.source();
                this.resetRepairData();
                const formData = new FormData();
                formData.append('file', file);
                this.loading = true;
                this.progress = 0;

                const response = await uploadMusicCheckFix({
                    formData,
                    params: {
                        wavOnly: true,
                    },
                    cancelToken: this.uploadSource.token,
                    onUploadProgress: (progressEvent) => {
                        if (!this.cancelled) {
                            this.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        }
                    }
                });

                if (this.cancelled) return;
                
                this.loading = false;
                this.progress = 0;
                
                const {state, result, msg} = response.data;

                if (!state) {
                    this.repairStatus = 'failed';
                    this.errorMsg = msg || null;
                    this.loading = false;
                    this.progress = 0;
                    this.repairLoading = false;
                    const fileName = file.name.substring(0, file.name.lastIndexOf('.'));
                    const format = file.name.substring(file.name.lastIndexOf('.') + 1);
                    this.fileNmae = fileName;
                    this.tempAudioInfo = {
                        fileName: fileName,
                        format: format,
                        size: file.size,
                        path: null
                    };
                    const tempEmitData = {...this.tarckData,
                        fileName: file.name,
                        format: format,
                        size: file.size,
                        path: null
                    };
                    if (this.tarckData.name) tempEmitData.name = this.tarckData.name;
                    this.resetUploadData();
                    if (!this.cancelled) {
                        this.$emit('update:tarckData', JSON.parse(JSON.stringify(tempEmitData)));
                    }
                    return;
                }

                if (this.cancelled) return;

                const name = file.name.substring(0, file.name.lastIndexOf('.'));
                this.fileNmae = name;
                await this.getId3Info({file}, name);
                
                if (this.cancelled) return;

                const uploadResult = result || {};
                const taskId = uploadResult.id;
                const initStatus = uploadResult.status;
                let finalStatus = initStatus;

                const audioInfo = {...(uploadResult.audioInfo || {})};
                const originalAudioInfo = {...(uploadResult.originalAudioInfo || {})};

                const origFileData = {
                    fileName: (originalAudioInfo.fileName || file.name).replace(/\.[^.]+$/, ''),
                    size: originalAudioInfo.size,
                    format: originalAudioInfo.format,
                    khz: originalAudioInfo.khz,
                    kbps: originalAudioInfo.kbs,
                    duration: originalAudioInfo.duration,
                    bits: originalAudioInfo.bits,
                    channel: originalAudioInfo.channel,
                    quality: originalAudioInfo.quality,
                    path: null
                };
                this.tempAudioInfo = {...origFileData};

                if (this.cancelled) return;

                if (initStatus === 5) {
                    this.repairStatus = null;
                    const origPath = originalAudioInfo.originalUrl || originalAudioInfo.url || uploadResult.url;
                    const origFileName = originalAudioInfo.fileName || file.name;
                    const finalData = {...this.tarckData, ...audioKeyMap({
                        url: origPath,
                        size: originalAudioInfo.size,
                        format: originalAudioInfo.format,
                        khz: originalAudioInfo.khz,
                        kbs: originalAudioInfo.kbs,
                        duration: originalAudioInfo.duration,
                        bits: originalAudioInfo.bits,
                        fileName: origFileName.endsWith(`.${originalAudioInfo.format}`) ? origFileName : (origFileName + '.' + originalAudioInfo.format),
                        channel: originalAudioInfo.channel,
                        quality: originalAudioInfo.quality,
                    })};
                    if (this.tarckData.name) finalData.name = this.tarckData.name;
                    this.resetUploadData();
                    if (!this.cancelled) {
                        this.$emit('update:tarckData', JSON.parse(JSON.stringify(finalData)));
                        this.$emit('onSuccess', JSON.parse(JSON.stringify(finalData)));
                    }
                    return;
                } else if (initStatus === 2) {
                    this.repairStatus = 'repaired';
                    this.repairMsg = '音频结构已修复，音质不变';
                    this.$message.success('音频文件结构已自动修复，音质不变');
                    if (!audioInfo.convertedPath && (uploadResult.convertedPath || uploadResult.url)) {
                        audioInfo.convertedPath = uploadResult.convertedPath || uploadResult.url;
                    }
                } else if (initStatus === 0 || initStatus === 1) {
                    this.repairLoading = true;
                    this.repairStatus = 'repairing';
                    this.repairMsg = initStatus === 1 ? '音频文件结构不规范，系统正在尝试自动修复' : '音频检测中，请稍候';
                    const tempEmitData = {...this.tarckData, ...origFileData};
                    if (this.tarckData.name) tempEmitData.name = this.tarckData.name;
                    if (!this.cancelled) {
                        this.$emit('update:tarckData', JSON.parse(JSON.stringify(tempEmitData)));
                    }

                    const pollResult = await this.pollMusicCheckFixStatus(taskId, origFileData);
                    this.repairLoading = false;
                    if (!pollResult || this.cancelled) {
                        return;
                    }
                    finalStatus = pollResult.status;
                    if (pollResult.audioInfo) Object.assign(audioInfo, pollResult.audioInfo);
                    if (pollResult.originalAudioInfo) Object.assign(originalAudioInfo, pollResult.originalAudioInfo);
                    if (finalStatus === 2 && !audioInfo.convertedPath) {
                        audioInfo.convertedPath = pollResult.convertedPath || pollResult.url;
                    }
                    if (finalStatus === 5 && !(originalAudioInfo.originalUrl || originalAudioInfo.url)) {
                        originalAudioInfo.originalUrl = pollResult.originalUrl || pollResult.url;
                    }
                } else if (initStatus === 3 || initStatus === 4) {
                    this.repairStatus = 'failed';
                    this.errorMsg = null;
                    const tempEmitData = {...this.tarckData, ...origFileData};
                    if (this.tarckData.name) tempEmitData.name = this.tarckData.name;
                    this.resetUploadData();
                    if (!this.cancelled) {
                        this.$emit('update:tarckData', JSON.parse(JSON.stringify(tempEmitData)));
                    }
                    return;
                }

                if (this.cancelled) return;

                const isFixed = finalStatus === 2;
                const info = isFixed ? audioInfo : originalAudioInfo;
                const path = isFixed ? (info.convertedPath || info.url || uploadResult.convertedPath || uploadResult.url) : (info.originalUrl || info.url);
                const metaInfo = (info.size || info.fileName) ? info : originalAudioInfo;
                const finalFileName = metaInfo.fileName || origFileData.fileName;
                
                const audioData = {...this.tarckData, ...audioKeyMap({
                    url: path,
                    size: metaInfo.size,
                    format: metaInfo.format,
                    khz: metaInfo.khz,
                    kbs: metaInfo.kbs,
                    duration: metaInfo.duration,
                    bits: metaInfo.bits,
                    fileName: finalFileName.endsWith(`.${metaInfo.format}`) ? finalFileName : (finalFileName + '.' + metaInfo.format),
                    channel: metaInfo.channel,
                    quality: metaInfo.quality,
                })};
                if (this.tarckData.name) audioData.name = this.tarckData.name;
                this.resetUploadData();
                if (!this.cancelled) {
                    this.$emit('update:tarckData', JSON.parse(JSON.stringify(audioData)));
                    this.$emit('onSuccess', JSON.parse(JSON.stringify(audioData)));
                }
            } catch (error) {
                if (axios.isCancel(error)) {
                    return;
                }
                console.error(error);
                this.resetUploadData();
                this.repairLoading = false;
                if (!this.cancelled) {
                    this.$message.error('上传失败，请重试');
                }
            }
        },
        async pollMusicCheckFixStatus(id, origFileData) {
            const maxAttempts = 120;
            const interval = 2000;
            const pollSource = axios.CancelToken.source();

            for (let i = 0; i < maxAttempts; i++) {
                await new Promise(resolve => setTimeout(resolve, interval));

                if (this.pollCancelled || this.cancelled) {
                    return null;
                }

                try {
                    const res = await fetchMusicCheckFixStatus({id}, pollSource.token);
                    
                    if (this.pollCancelled || this.cancelled) {
                        return null;
                    }

                    if (!res.data.state) {
                        this.errorMsg = res.data.msg || null;
                        this.repairStatus = 'failed';
                        this.repairLoading = false;
                        const tempEmitData = {...this.tarckData, ...origFileData};
                        if (this.tarckData.name) tempEmitData.name = this.tarckData.name;
                        if (!this.cancelled) {
                            this.$emit('update:tarckData', JSON.parse(JSON.stringify(tempEmitData)));
                        }
                        return null;
                    }

                    const result = res.data.result || {};
                    const status = result.status;

                    if (status === 2) {
                        this.repairStatus = 'repaired';
                        this.repairMsg = '音频结构已修复，音质不变';
                        this.$message.success('音频文件结构已自动修复，音质不变');
                        return result;
                    } else if (status === 5) {
                        this.repairStatus = null;
                        this.repairMsg = '';
                        return result;
                    } else if (status === 3 || status === 4) {
                        this.repairStatus = 'failed';
                        this.errorMsg = null;
                        const tempEmitData = {...this.tarckData, ...origFileData};
                        if (this.tarckData.name) tempEmitData.name = this.tarckData.name;
                        if (!this.cancelled) {
                            this.$emit('update:tarckData', JSON.parse(JSON.stringify(tempEmitData)));
                        }
                        return null;
                    }

                    this.repairMsg = status === 1 ? '音频文件结构不规范，系统正在尝试自动修复' : '音频检测中，请稍候';
                } catch (error) {
                    if (axios.isCancel(error) || this.cancelled || this.pollCancelled) {
                        return null;
                    }
                    throw error;
                }
            }

            if (this.cancelled || this.pollCancelled) {
                return null;
            }

            this.repairStatus = 'failed';
            this.errorMsg = null;
            const tempEmitData = {...this.tarckData, ...origFileData};
            if (this.tarckData.name) tempEmitData.name = this.tarckData.name;
            if (!this.cancelled) {
                this.$emit('update:tarckData', JSON.parse(JSON.stringify(tempEmitData)));
            }
            return null;
        },
        beforeUploadHandle(file) {
            return audioBeforeUploadHandle(file, this);
        },
        onEdit() {
            this.$refs.audioUpload.$refs['upload-inner'].$refs.input.click();
        },
        onDelete() {
            const {tarckData} = this;
            const data = JSON.parse(JSON.stringify(tarckData));
            data.path = '';
            this.resetRepairData();
            this.$emit('update:tarckData', data);
        },
    },
};
</script>
<style lang="scss">
.audio-upload-wrap {
    background: $--input-background-color;
    min-height: 148px;
    border: 1px dashed $--border-color-base;
    border-radius: 4px;
    overflow: hidden;
    position: relative;

    &:hover {
        border: 1px dashed $-text-950;
    }

    .upload {
        position: relative;

        .el-upload {
            display: block;
            width: 100%;
            position: relative;
            z-index: 1;

            .el-upload-dragger {
                align-items: center;
                height: 148px;
                width: 100%;
                border: none;
                background: $--input-background-color;

                .audio-content {
                    display: flex;
                    align-items: center;
                    height: 148px;

                    .icon {
                        width: 148px;
                        height: 148px;
                        background: url('~@/assets/img/upload/zhan_music@2x.png') no-repeat center;
                        background-size: cover;
                    }

                    .content {
                        flex: 1;
                        margin: 0 20px;

                        .describe {
                            font-size: 12px;
                            color: $-text-500;
                            line-height: 24px;
                            text-align: left;

                            .text {
                                color: $-text-950;
                            }
                        }

                        .button {
                            align-items: center;
                            display: none;

                            .point {
                                flex: 1;
                                position: relative;
                                height: 4px;
                                overflow: hidden;
                                margin-right: 10px;

                                &::before {
                                    content: '..............';
                                    position: absolute;
                                    top: -10px;
                                    left: 0px;
                                    color: $-text-950;
                                    line-height: 16px;
                                    font-weight: bold;
                                    font-size: 16px;
                                    letter-spacing: 3px;
                                }
                            }
                        }
                    }

                    &:hover {
                        .button {
                            display: flex;
                        }

                        .describe {
                            display: none;
                        }
                    }
                }
            }
        }
    }

    .loading {
        padding: 49px 50px;
        text-align: center;

        .el-slider {
            .el-slider__runway {
                .el-slider__bar {
                    background-color: $--color-base;
                }

                .el-slider__button-wrapper {
                    top: -16px;

                    .el-slider__button {
                        border: solid 2px $--color-base;
                        background-color: $--color-base;
                    }
                }
            }
        }

        p.name {
            font-size: 12px;
            color: $-text-500;
            line-height: 12px;
        }
    }
}
</style>
